import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Heading,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Input,
  FormControl,
  FormLabel,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { supabase } from '../../lib/supabaseClient';
import Papa from 'papaparse';

export default function AdminVideoSettlement() {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedData, setUploadedData] = useState([]);
  const [stats, setStats] = useState(null);
  const [channelName, setChannelName] = useState('');
  const [payoutMonth, setPayoutMonth] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [uploadMode, setUploadMode] = useState('direct'); // 'youtube' or 'direct'
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef();
  const toast = useToast();

  // 배치 저장 함수
  const saveBatch = async (data, batchSize = 100) => {
    const batches = [];
    for (let i = 0; i < data.length; i += batchSize) {
      batches.push(data.slice(i, i + batchSize));
    }

    let savedCount = 0;
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      const { error } = await supabase
        .from('payout_data_video_expact_test')
        .insert(batch);

      if (error) {
        throw new Error(`배치 ${i + 1} 저장 실패: ${error.message}`);
      }

      savedCount += batch.length;
      setUploadProgress((savedCount / data.length) * 100);
    }

    return savedCount;
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: '오류',
        description: 'CSV 파일만 업로드 가능합니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSelectedFile(file);
    toast({
      title: '파일 선택됨',
      description: `${file.name} 파일이 선택되었습니다. 시작 버튼을 눌러 업로드를 시작하세요.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleStartUpload = async () => {
    if (!selectedFile) {
      toast({
        title: '파일 미선택',
        description: '업로드할 파일을 먼저 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!payoutMonth.trim()) {
      toast({
        title: '정산월 필요',
        description: '정산월을 입력해주세요. (예: 202506)',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (uploadMode === 'youtube' && !channelName.trim()) {
      toast({
        title: '채널명 필요',
        description: '채널명을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // CSV 파일 파싱
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          // 501행 오류는 제외하고 실제 오류만 필터링
          const filteredErrors = results.errors.filter(error => error.row !== 501);
          
          if (filteredErrors.length > 0) {
            console.error('파싱 오류 상세:', filteredErrors);
            const errorDetails = filteredErrors.map((error, index) => 
              `오류 ${index + 1}: 행 ${error.row + 1}, 컬럼 "${error.type}", 메시지: ${error.message}`
            ).join('\n');
            
            toast({
              title: '파싱 오류',
              description: `CSV 파일 파싱 중 오류가 발생했습니다.\n\n${errorDetails}`,
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
            setUploading(false);
            return;
          }

          let mappedData = [];

          if (uploadMode === 'youtube') {
            // 기존 YouTube Studio CSV 처리
            // '합계'가 포함된 행 필터링
            const filteredData = results.data.filter(row => 
              !row['콘텐츠'] || (!row['콘텐츠'].includes('합계') && !row['콘텐츠'].includes('상위 500개 결과 표시'))
            );

            if (filteredData.length === 0) {
              toast({
                title: '데이터 없음',
                description: '업로드할 데이터가 없습니다.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
              });
              setUploading(false);
              return;
            }

            setUploadProgress(10);

            // licensed_video 테이블에서 데이터 가져오기
            const { data: licensedVideos, error: licensedError } = await supabase
              .from('licensed_video')
              .select('id, title, identification_code, company, share_video_laeebly, share_video_creator, share_video_copyright, share_shopping_laeebly, share_shopping_creator, share_shopping_copyright');

            if (licensedError) {
              console.error('라이센스 비디오 데이터 가져오기 실패:', licensedError);
              toast({
                title: '오류',
                description: '라이센스 비디오 데이터를 가져오는 중 오류가 발생했습니다.',
                status: 'error',
                duration: 3000,
                isClosable: true,
              });
              setUploading(false);
              return;
            }

            setUploadProgress(20);

            // 데이터 매핑 및 변환 + 라이센스 비디오 매칭 + 수익 분배 계산
            mappedData = filteredData.map((row, index) => {
              setUploadProgress(20 + (index / filteredData.length) * 60);
              
              // 빈 문자열을 null로 변환하는 헬퍼 함수
              const parseValue = (value, parser = (v) => v) => {
                if (!value || value === '' || value === 'null') return null;
                return parser(value);
              };

              // 기본 데이터 매핑
              const basicData = {
                channel_name: channelName.trim(),
                payout_month: payoutMonth.trim(),
                content_id: row['콘텐츠'] || '',
                video_title: row['동영상 제목'] || '',
                publish_time: parseValue(row['동영상 게시 시간']),
                video_length: row['길이'] || '',
                valid_views: parseValue(row['유효 조회수']?.replace(/,/g, ''), parseInt) || 0,
                views: parseValue(row['조회수']?.replace(/,/g, ''), parseInt) || 0,
                watch_time_hours: parseValue(row['시청 시간(단위: 시간)']?.replace(/,/g, ''), parseFloat) || 0,
                subscribers: parseValue(row['구독자']?.replace(/,/g, ''), parseInt) || 0,
                impressions: parseValue(row['노출수']?.replace(/,/g, ''), parseInt) || 0,
                impression_click_rate: parseValue(row['노출 클릭률 (%)']?.replace(/,/g, ''), parseFloat) || 0,
                profits_krw: parseValue(row['예상 수익 (KRW)']?.replace(/,/g, ''), parseFloat) || 0,
                total_amount: parseValue(row['총매출 (KRW)']?.replace(/,/g, ''), parseFloat) || 0,
                orders_count: parseValue(row['주문']?.replace(/,/g, ''), parseFloat) || 0,
                pending_revenue: parseValue(row['대기 중인 수수료 (KRW)']?.replace(/,/g, ''), parseFloat) || 0,
                returned_revenue: parseValue(row['반품된 제품 (KRW)']?.replace(/,/g, ''), parseFloat) || 0,
                approved_revenue: parseValue(row['승인된 수수료 (KRW)']?.replace(/,/g, ''), parseFloat) || 0,
                youtube_premium_views: parseValue(row['YouTube Premium 조회수']?.replace(/,/g, ''), parseFloat) || 0,
                youtube_premium_watch_time_hours: parseValue(row['YouTube Premium 시청 시간(단위: 분)']?.replace(/,/g, ''), (v) => parseFloat(v) / 60) || 0,
                created_at: new Date().toISOString(),
                upload_at: new Date().toISOString(),
              };

              // 라이센스 비디오 매칭
              let matchedVideo = null;
              let identificationCode = null;
              let licensedVideoTitle = null;
              let company = null;

              // 1. identification_code 매칭 (5자리 문자열)
              const idMatch = basicData.video_title.match(/#([A-Za-z0-9]{5})/);
              if (idMatch) {
                const code = idMatch[1];
                matchedVideo = licensedVideos.find(video => video.identification_code === code);
                if (matchedVideo) {
                  identificationCode = code;
                  licensedVideoTitle = matchedVideo.title;
                  company = matchedVideo.company;
                }
              }

              // 2. 제목 포함 매칭 (identification_code 매칭이 실패한 경우)
              if (!matchedVideo) {
                matchedVideo = licensedVideos.find(video => 
                  basicData.video_title.replace(/\s/g, '').includes(video.title.replace(/\s/g, ''))
                );
                if (matchedVideo) {
                  identificationCode = matchedVideo.identification_code;
                  licensedVideoTitle = matchedVideo.title;
                  company = matchedVideo.company;
                }
              }

              // 3. 수익 분배 계산
              let laeeblyRevenue = 0;
              let creatorRevenue = 0;
              let copyrightsRevenue = 0;
              let laeeblyShoppingRevenue = 0;
              let creatorShoppingRevenue = 0;
              let copyrightsShoppingRevenue = 0;

              if (matchedVideo) {
                // 영상 수익 분배
                laeeblyRevenue = ((basicData.profits_krw - basicData.pending_revenue || 0) * (matchedVideo.share_video_laeebly || 0) / 100);
                creatorRevenue = ((basicData.profits_krw - basicData.pending_revenue || 0) * (matchedVideo.share_video_creator || 0) / 100);
                copyrightsRevenue = ((basicData.profits_krw - basicData.pending_revenue || 0) * (matchedVideo.share_video_copyright || 0) / 100);

                // 쇼핑 수익 분배
                laeeblyShoppingRevenue = ((basicData.pending_revenue || 0) * (matchedVideo.share_shopping_laeebly || 0) / 100);
                creatorShoppingRevenue = ((basicData.pending_revenue || 0) * (matchedVideo.share_shopping_creator || 0) / 100);
                copyrightsShoppingRevenue = ((basicData.pending_revenue || 0) * (matchedVideo.share_shopping_copyright || 0) / 100);
              }

              return {
                ...basicData,
                company: company,
                identification_code: identificationCode,
                licensed_video_title: licensedVideoTitle,
                laeebly_revenue: laeeblyRevenue,
                creator_revenue: creatorRevenue,
                copyrights_revenue: copyrightsRevenue,
                laeebly_shopping_revenue: laeeblyShoppingRevenue,
                creator_shopping_revenue: creatorShoppingRevenue,
                copyrights_shopping_revenue: copyrightsShoppingRevenue,
              };
            });
          } else {
            // 새로운 직접 CSV 처리
            const filteredData = results.data.filter(row => 
              row.channel_name && row.content_id // 최소한의 유효성 검사
            );

            if (filteredData.length === 0) {
              toast({
                title: '데이터 없음',
                description: '업로드할 데이터가 없습니다.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
              });
              setUploading(false);
              return;
            }

            setUploadProgress(20);

            // 빈 문자열을 null로 변환하는 헬퍼 함수
            const parseValue = (value, parser = (v) => v) => {
              if (!value || value === '' || value === 'null') return null;
              return parser(value);
            };

            // 데이터 매핑 (id 제외)
            mappedData = filteredData.map((row, index) => {
              setUploadProgress(20 + (index / filteredData.length) * 60);
              
              return {
                channel_name: row.channel_name || '',
                content_id: row.content_id || '',
                video_title: row.video_title || '',
                publish_time: parseValue(row.publish_time),
                video_length: row.video_length || '',
                valid_views: parseValue(row.valid_views, parseInt) || 0,
                views: parseValue(row.views, parseInt) || 0,
                watch_time_hours: parseValue(row.watch_time_hours, parseFloat) || 0,
                subscribers: parseValue(row.subscribers, parseInt) || 0,
                impressions: parseValue(row.impressions, parseInt) || 0,
                impression_click_rate: parseValue(row.impression_click_rate, parseFloat) || 0,
                created_at: new Date().toISOString(),
                profits_krw: parseValue(row.profits_krw, parseFloat) || 0,
                upload_at: new Date().toISOString(),
                identification_code: row.identification_code || null,
                licensed_video_title: row.licensed_video_title || null,
                company: row.company || null,
                total_amount: parseValue(row.total_amount, parseFloat) || 0,
                orders_count: parseValue(row.orders_count, parseFloat) || 0,
                pending_revenue: parseValue(row.pending_revenue, parseFloat) || 0,
                returned_revenue: parseValue(row.returned_revenue, parseFloat) || 0,
                laeebly_revenue: parseValue(row.laeebly_revenue, parseFloat) || 0,
                creator_revenue: parseValue(row.creator_revenue, parseFloat) || 0,
                copyrights_revenue: parseValue(row.copyrights_revenue, parseFloat) || 0,
                laeebly_shopping_revenue: parseValue(row.laeebly_shopping_revenue, parseFloat) || 0,
                creator_shopping_revenue: parseValue(row.creator_shopping_revenue, parseFloat) || 0,
                copyrights_shopping_revenue: parseValue(row.copyrights_shopping_revenue, parseFloat) || 0,
                approved_revenue: parseValue(row.approved_revenue, parseFloat) || 0,
                shopping_click_number: parseValue(row.shopping_click_number, parseFloat) || 0,
                shopping_impression_number: parseValue(row.shopping_impression_number, parseFloat) || 0,
                youtube_premium_views: parseValue(row.youtube_premium_views, parseFloat) || 0,
                youtube_premium_watch_time_hours: parseValue(row.youtube_premium_watch_time_hours, parseFloat) || 0,
                payout_month: payoutMonth.trim(),
              };
            });
          }

          setUploadProgress(80);

          // 배치로 데이터베이스에 저장
          try {
            const savedCount = await saveBatch(mappedData, 100);
            
            setUploadProgress(100);

            // 통계 계산
            const matchedCount = uploadMode === 'youtube' ? mappedData.filter(item => item.identification_code).length : 0;
            const totalRevenue = mappedData.reduce((sum, item) => sum + (item.profits_krw || 0), 0);
            const totalViews = mappedData.reduce((sum, item) => sum + (item.views || 0), 0);
            const totalValidViews = mappedData.reduce((sum, item) => sum + (item.valid_views || 0), 0);
            const totalWatchTime = mappedData.reduce((sum, item) => sum + (item.watch_time_hours || 0), 0);
            const totalShoppingRevenue = mappedData.reduce((sum, item) => sum + (item.pending_revenue || 0), 0);

            setStats({
              totalRecords: mappedData.length,
              matchedCount,
              totalRevenue,
              totalViews,
              totalValidViews,
              totalWatchTime,
              totalShoppingRevenue,
            });

            setUploadedData(mappedData);

            toast({
              title: '업로드 완료',
              description: `${mappedData.length}개의 레코드가 성공적으로 업로드되었습니다.${uploadMode === 'youtube' ? ` (${matchedCount}개 매칭됨)` : ''}`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });

          } catch (error) {
            console.error('배치 저장 오류:', error);
            toast({
              title: '저장 오류',
              description: `데이터 저장 중 오류가 발생했습니다: ${error.message}`,
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }

          setUploading(false);
        },
        error: (error) => {
          console.error('CSV 파싱 오류:', error);
          toast({
            title: '파싱 오류',
            description: `CSV 파일을 읽는 중 오류가 발생했습니다.\n\n오류 타입: ${error.type}\n메시지: ${error.message}`,
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
          setUploading(false);
        },
      });
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      toast({
        title: '업로드 오류',
        description: '파일 업로드 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setUploading(false);
    }
  };

  const clearData = () => {
    setUploadedData([]);
    setStats(null);
    setUploadProgress(0);
    setChannelName('');
    setPayoutMonth('');
    setCurrentPage(1);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 페이지네이션 계산
  const totalPages = Math.ceil(uploadedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = uploadedData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg" color="gray.700">영상 정산 데이터 업로드</Heading>
        
        {/* 업로드 모드 선택 */}
        <Card>
          <CardBody>
            <Tabs variant="enclosed" onChange={(index) => setUploadMode(index === 0 ? 'direct' : 'youtube')}>
              <TabList>
                <Tab>직접 CSV 업로드</Tab>
                <Tab>YouTube Studio CSV</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <VStack spacing={4}>
                    <Text fontSize="lg" fontWeight="bold">직접 CSV 업로드</Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      다음 컬럼을 포함한 CSV 파일을 업로드하세요:<br />
                      channel_name, content_id, video_title, publish_time, video_length, valid_views, views, watch_time_hours, subscribers, impressions, impression_click_rate, created_at, profits_krw, upload_at, identification_code, licensed_video_title, company, total_amount, orders_count, pending_revenue, returned_revenue, laeebly_revenue, creator_revenue, copyrights_revenue, laeebly_shopping_revenue, creator_shopping_revenue, copyrights_shopping_revenue, approved_revenue, shopping_click_number, shopping_impression_number, youtube_premium_views, youtube_premium_watch_time_hours<br />
                      <strong>id 컬럼은 자동으로 제외됩니다. 100개씩 배치로 저장됩니다.</strong>
                    </Text>
                  </VStack>
                </TabPanel>
                <TabPanel>
                  <VStack spacing={4}>
                    <Text fontSize="lg" fontWeight="bold">YouTube Studio CSV 업로드</Text>
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      YouTube Studio에서 다운로드한 CSV 파일을 업로드하세요.<br />
                      파일 형식: 콘텐츠, 동영상 제목, 동영상 게시 시간, 길이, 유효 조회수, YouTube Premium 시청 시간(단위: 분), 
                      YouTube Premium 조회수, 주문, 반품된 제품 (KRW), 대기 중인 수수료 (KRW), 총매출 (KRW), 
                      승인된 수수료 (KRW), 조회수, 시청 시간(단위: 시간), 구독자, 예상 수익 (KRW), 노출수, 노출 클릭률 (%)<br />
                      <strong>업로드 시 자동으로 라이센스 비디오와 매칭하고 수익 분배를 계산합니다. 회사 정보는 매칭된 라이센스 비디오에서 자동으로 가져옵니다.</strong>
                    </Text>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
        
        {/* 입력 필드 섹션 */}
        <Card>
          <CardBody>
            <VStack spacing={4}>
              <Text fontSize="lg" fontWeight="bold">기본 정보 입력</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                {uploadMode === 'youtube' && (
                  <FormControl isRequired>
                    <FormLabel>채널명</FormLabel>
                    <Input
                      placeholder="채널명을 입력하세요"
                      value={channelName}
                      onChange={(e) => setChannelName(e.target.value)}
                    />
                  </FormControl>
                )}
                <FormControl isRequired>
                  <FormLabel>정산월</FormLabel>
                  <Input
                    placeholder="202506 (YYYYMM 형식)"
                    value={payoutMonth}
                    onChange={(e) => setPayoutMonth(e.target.value)}
                    maxLength={6}
                  />
                </FormControl>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
        
        {/* 업로드 섹션 */}
        <Card>
          <CardBody>
            <VStack spacing={4}>
              <Text fontSize="lg" fontWeight="bold">CSV 파일 업로드</Text>
              
              <HStack spacing={4}>
                <Button
                  colorScheme="blue"
                  onClick={() => fileInputRef.current?.click()}
                  isLoading={uploading}
                  loadingText="업로드 및 계산 중..."
                >
                  파일 선택
                </Button>
                {selectedFile && !uploading && (
                  <Button
                    colorScheme="green"
                    onClick={handleStartUpload}
                    isLoading={uploading}
                    loadingText="처리 중..."
                  >
                    시작
                  </Button>
                )}
                {uploadedData.length > 0 && (
                  <Button
                    colorScheme="gray"
                    onClick={clearData}
                  >
                    초기화
                  </Button>
                )}
              </HStack>
              
              {selectedFile && (
                <Text fontSize="sm" color="green.600" textAlign="center">
                  선택된 파일: {selectedFile.name}
                </Text>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              
              {uploading && (
                <Box w="100%">
                  <Progress value={uploadProgress} colorScheme="blue" size="lg" />
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    업로드 및 계산 진행률: {Math.round(uploadProgress)}%
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* 통계 섹션 */}
        {stats && (
          <Card>
            <CardBody>
              <Text fontSize="lg" fontWeight="bold" mb={4}>업로드 통계</Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Stat>
                  <StatLabel>총 레코드</StatLabel>
                  <StatNumber>{stats.totalRecords.toLocaleString()}</StatNumber>
                  <StatHelpText>업로드된 데이터</StatHelpText>
                </Stat>
                {uploadMode === 'youtube' && (
                  <Stat>
                    <StatLabel>매칭된 영상</StatLabel>
                    <StatNumber>{stats.matchedCount.toLocaleString()}</StatNumber>
                    <StatHelpText>라이센스 비디오와 매칭</StatHelpText>
                  </Stat>
                )}
                <Stat>
                  <StatLabel>총 수익</StatLabel>
                  <StatNumber>₩{stats.totalRevenue.toLocaleString()}</StatNumber>
                  <StatHelpText>예상 수익 합계</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>총 쇼핑 수익</StatLabel>
                  <StatNumber>₩{stats.totalShoppingRevenue.toLocaleString()}</StatNumber>
                  <StatHelpText>승인된 수수료 합계</StatHelpText>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* 업로드된 데이터 테이블 */}
        {uploadedData.length > 0 && (
          <Card>
            <CardBody>
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="lg" fontWeight="bold">업로드된 데이터 미리보기</Text>
                <Text fontSize="sm" color="gray.600">
                  {uploadedData.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, uploadedData.length)} / ${uploadedData.length}개 영상` : '영상이 없습니다'}
                </Text>
              </Flex>
              <Box overflowX="auto">
                <Table size="sm" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>동영상 제목</Th>
                      <Th>콘텐츠 ID</Th>
                      <Th>식별 코드</Th>
                      <Th>라이센스 제목</Th>
                      <Th>회사</Th>
                      <Th isNumeric>조회수</Th>
                      <Th isNumeric>유효 조회수</Th>
                      <Th isNumeric>예상 수익 (KRW)</Th>
                      <Th isNumeric>총매출 (KRW)</Th>
                      <Th isNumeric>시청시간 (h)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentData.map((item, index) => (
                      <Tr key={startIndex + index}>
                        <Td maxW="200px" noOfLines={2}>{item.video_title}</Td>
                        <Td>{item.content_id}</Td>
                        <Td>{item.identification_code || '-'}</Td>
                        <Td maxW="150px" noOfLines={2}>{item.licensed_video_title || '-'}</Td>
                        <Td>{item.company || '-'}</Td>
                        <Td isNumeric>{item.views?.toLocaleString()}</Td>
                        <Td isNumeric>{item.valid_views?.toLocaleString()}</Td>
                        <Td isNumeric>₩{item.profits_krw?.toLocaleString()}</Td>
                        <Td isNumeric>₩{item.total_amount?.toLocaleString()}</Td>
                        <Td isNumeric>{item.watch_time_hours?.toFixed(1)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              
              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <Flex justify="center" align="center" mt={6} gap={2}>
                  <Button
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    isDisabled={currentPage === 1}
                    colorScheme="blue"
                    variant="outline"
                  >
                    처음
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    isDisabled={currentPage === 1}
                    colorScheme="blue"
                    variant="outline"
                  >
                    이전
                  </Button>
                  
                  <HStack spacing={1}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          colorScheme={currentPage === pageNum ? "blue" : "gray"}
                          variant={currentPage === pageNum ? "solid" : "outline"}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </HStack>
                  
                  <Button
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    isDisabled={currentPage === totalPages}
                    colorScheme="blue"
                    variant="outline"
                  >
                    다음
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    isDisabled={currentPage === totalPages}
                    colorScheme="blue"
                    variant="outline"
                  >
                    마지막
                  </Button>
                </Flex>
              )}
            </CardBody>
          </Card>
        )}

        {/* 안내 메시지 */}
        <Alert status="info">
          <AlertIcon />
          <Box>
            <AlertTitle>주의사항</AlertTitle>
            <AlertDescription>
              {uploadMode === 'youtube' ? (
                <>
                  • CSV 파일의 '합계' 행은 자동으로 제외됩니다.<br />
                  • 숫자 데이터는 자동으로 변환됩니다 (쉼표 제거, 타입 변환).<br />
                  • YouTube Premium 시청시간은 분에서 시간으로 자동 변환됩니다.<br />
                  • 업로드 시 자동으로 라이센스 비디오와 매칭하고 수익 분배를 계산합니다.<br />
                  • 회사 정보는 매칭된 라이센스 비디오에서 자동으로 가져옵니다.<br />
                  • 업로드된 데이터는 payout_data_video_expact 테이블에 저장됩니다.
                </>
              ) : (
                <>
                  • CSV 파일의 id 컬럼은 자동으로 제외됩니다.<br />
                  • 숫자 데이터는 자동으로 변환됩니다 (쉼표 제거, 타입 변환).<br />
                  • 데이터는 100개씩 배치로 저장됩니다.<br />
                  • 업로드된 데이터는 payout_data_video_expact 테이블에 저장됩니다.
                </>
              )}
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>
    </Box>
  );
} 